"use client";

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./App.css";
import AssessmentList from "./components/AssessmentList";
import QuestionSelector from "./components/QuestionSelector";
import QuestionDisplay from "./components/QuestionDisplay";
import NetIdInput from "./components/NetIdInput";
import StudentInsight from "./components/StudentInsight";
// import StudentInsightModal from "./components/StudentInsightModal";
import StudentOverallInsightModal from "./components/StudentOverallInsightModal";
import { useUser } from "./contexts/UserContext";
import { API_URL } from "./config";
import SQLEditor from "./components/SQLEditor";

function AssessmentPage() {
  const { assessmentId, questionId } = useParams();
  const navigate = useNavigate();
  const { user, updateUser } = useUser();
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [submission, setSubmission] = useState("");
  const [feedback, setFeedback] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingAssessment, setIsLoadingAssessment] = useState(false);
  const [isLoadingQuestion, setIsLoadingQuestion] = useState(false);
  const [error, setError] = useState("");
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [netId, setNetId] = useState("");
  const [isNetIdSubmitted, setIsNetIdSubmitted] = useState(false);
  const [showInsightModal, setShowInsightModal] = useState(false);

  // Initialize netId from user data
  useEffect(() => {
    if (user && user.net_id) {
      setNetId(user.net_id);
      setIsNetIdSubmitted(true);
    } else if (user) {
      // If user exists but net_id is null, need to show the NetIdInput
      setIsNetIdSubmitted(false);
    }
  }, [user]);

  // Function to get the auth header for API calls
  const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Load assessment data when assessmentId changes
  useEffect(() => {
    const loadAssessment = async () => {
      if (assessmentId) {
        setIsLoadingAssessment(true);
        try {
          const response = await fetch(`${API_URL}/api/assessments`, {
            headers: {
              ...getAuthHeader(),
              "Content-Type": "application/json",
            },
          });
          if (!response.ok) {
            if (response.status === 401) {
              navigate("/");
              return;
            }
            throw new Error("Failed to fetch assessments");
          }
          const data = await response.json();
          const assessment = data.assessments.find(
            (a) => a.id === assessmentId
          );
          if (assessment) {
            setSelectedAssessment(assessment);
          } else {
            // If assessment not found, redirect to assessments list
            navigate("/assessments");
          }
        } catch (error) {
          console.error("Error loading assessment:", error);
          navigate("/assessments");
        } finally {
          setIsLoadingAssessment(false);
        }
      } else {
        setSelectedAssessment(null);
      }
    };

    loadAssessment();
  }, [assessmentId, navigate]);

  // Load question data when questionId changes
  useEffect(() => {
    const loadQuestion = async () => {
      if (assessmentId && questionId) {
        setIsLoadingQuestion(true);
        try {
          // Build the questions URL with optional netId parameter
          let questionsUrl = `${API_URL}/api/assessment/${assessmentId}/questions`;
          if (netId) {
            questionsUrl += `?netId=${encodeURIComponent(netId)}`;
          }

          const response = await fetch(questionsUrl, {
            headers: {
              ...getAuthHeader(),
              "Content-Type": "application/json",
            },
          });

          if (!response.ok) {
            if (response.status === 401) {
              // Handle unauthorized
              navigate("/");
              return;
            }
            throw new Error("Failed to fetch questions");
          }

          const data = await response.json();
          const question = data.questions.find((q) => q.id === questionId);
          if (question) {
            setSelectedQuestion(question);
          } else {
            // If question not found, redirect to assessment questions
            navigate(`/assessments/${assessmentId}`);
          }
        } catch (error) {
          console.error("Error loading question:", error);
          navigate(`/assessments/${assessmentId}`);
        } finally {
          setIsLoadingQuestion(false);
        }
      } else {
        setSelectedQuestion(null);
      }
    };

    loadQuestion();
  }, [assessmentId, questionId, netId, navigate]);

  const handleNetIdSubmit = (id) => {
    setNetId(id);
    setIsNetIdSubmitted(true);

    // Update user data with the new netId
    if (user) {
      updateUser({ net_id: id });
    }
  };

  const handleSelectAssessment = (assessment) => {
    if (assessment && assessment.id) {
      setSelectedAssessment(assessment);
      setSelectedQuestion(null);
      navigate(`/assessments/${assessment.id}`);
    }
  };

  const handleBackToAssessments = () => {
    setSelectedAssessment(null);
    setSelectedQuestion(null);
    setSubmission("");
    setFeedback("");
    setError("");
    navigate("/assessments");
  };

  const handleSelectQuestion = (question) => {
    if (
      question &&
      question.id &&
      selectedAssessment &&
      selectedAssessment.id
    ) {
      setSelectedQuestion(question);
      setFeedback("");
      setError("");
      navigate(`/assessments/${selectedAssessment.id}/${question.id}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFeedback("");
    setError("");
    setIsLoading(true);

    if (!selectedQuestion) {
      setError("Please select a question first");
      setIsLoading(false);
      return;
    }

    if (!selectedAssessment) {
      setError("No assessment selected");
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/evaluate`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeader(),
        },
        body: JSON.stringify({
          submission,
          question_id: selectedQuestion.id,
          net_id: netId,
          assessment_id: selectedAssessment.id,
        }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Handle unauthorized
          navigate("/");
          return;
        }

        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error ${response.status}`);
      }

      const data = await response.json();
      setFeedback(data.feedback);
    } catch (error) {
      setError("Error connecting to evaluation service: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isNetIdSubmitted) {
    return <NetIdInput onSubmit={handleNetIdSubmit} />;
  }

  if (isLoadingAssessment || isLoadingQuestion) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="assessment-page">
      {!selectedAssessment ? (
        <AssessmentList onSelectAssessment={handleSelectAssessment} />
      ) : (
        <div className="assessment-workspace">
          {!selectedQuestion ? (
            <>
              <div className="navigation-header">
                <div className="nav-breadcrumb">
                  <button
                    className="nav-back-button"
                    onClick={handleBackToAssessments}
                  >
                    ←
                  </button>
                  <span className="nav-separator">/</span>
                  <span className="nav-current">Assessments</span>
                  <span className="nav-separator">/</span>
                </div>
              </div>

              <QuestionSelector
                assessmentId={selectedAssessment.id}
                onSelectQuestion={handleSelectQuestion}
                netId={netId}
              />
            </>
          ) : (
            <div className="question-workspace">
              <div className="navigation-header">
                <div className="nav-breadcrumb">
                  <button
                    className="nav-back-button"
                    onClick={() =>
                      navigate(`/assessments/${selectedAssessment.id}`)
                    }
                  >
                    ←
                  </button>
                  <span className="nav-separator">/</span>
                  <span className="nav-current">
                    {selectedAssessment.title}
                  </span>
                  <span className="nav-separator">/</span>
                </div>
              </div>

              <QuestionDisplay question={selectedQuestion} />

              <form onSubmit={handleSubmit}>
                <h3>Your Solution</h3>
                <SQLEditor
                  initialValue={submission}
                  onChange={(value) => setSubmission(value)}
                />

                <button
                  type="submit"
                  disabled={isLoading}
                  className="submit-button"
                >
                  {isLoading ? "Evaluating..." : "Submit"}
                </button>
              </form>

              {error && <div className="error-message">{error}</div>}

              {feedback && (
                <div className="feedback-container">
                  <h3>Feedback</h3>
                  <div className="feedback">{feedback}</div>
                </div>
              )}
              {/* Inline insight section */}
              {selectedQuestion && (
                <StudentInsight
                  assessmentId={selectedAssessment.id}
                  questionId={selectedQuestion.id}
                  netId={netId}
                />
              )}
            </div>
          )}

          <StudentOverallInsightModal
            assessmentId={selectedAssessment?.id}
            netId={netId}
            isOpen={showInsightModal}
            onClose={() => setShowInsightModal(false)}
          />
        </div>
      )}
    </div>
  );
}

export default AssessmentPage;
